"use client";

import React, { useEffect, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useParams, useRouter } from "next/navigation";
import Image from "next/image";
import InputHolder from "./inputHolder";
import MultiselectInputHolder from "./multiselectInputHolder";
import Textarea from "./textarea";
import SelectInputHolder from "./selectInputHolder";
import FileInputHolder from "./fileInputHolder";
import { validate } from "@/utils/validation";
import { postRequest } from "@/utils/postData";
import { getStrapiURL } from "@/utils/api-helpers";
import { googleRecaptcha } from "@/utils/googleRecaptcha";
import { getLeadTrackingData } from "@/utils/leadTracking";
import {
  getThankYouUrl,
  pushEnhancedConversionsData,
} from "@/utils/gtmEnhancedConversions";
import { FormType, ValidationErrors } from "../../../global";
import NotifiactionIcon from "@/app/assets/notification.svg";

interface FormInputs {
  name: string;
  mobileNumber: string;
  currentSalary: string;
  expectedSalary: string;
  description: string;
  howDidYouHearAboutUs: string;
  resume: File | null;
}

export default function CareerForm({ formData }: { formData: FormType }) {
  const router = useRouter();
  const params = useParams();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitError, setFormSubmitError] = useState(false);
  const [formInputs, setFormInputs] = useState<FormInputs | any>({
    name: "",
    mobileNumber: "",
    email: "",
    expectedSalary: "",
    description: "",
    howDidYouHearAboutUs: "",
    resume: null,
  });
  const [allInputsNotEmpty, setAllInputsNotEmpty] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    nameError: null,
    mobileNumberError: null,
    descriptionError: null,
    emailError: null,
    expectedSalaryError: null,
    howDidYouHearAboutUsError: null,
    resumeError: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  if (params.slug) {
    // convert from digital-account-excutive to Digital Account Executive
    const slug = (params.slug as string)
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    formInputs.role = slug;
  }

  const handleInputChange = async (name: any, value: any) => {
    if (name === "howDidYouHearAboutUsOther") {
      setFormInputs((prev: any) => ({
        ...prev,
        howDidYouHearAboutUs: `Others - ${value}`,
      }));
    } else {
      if (name === "howDidYouHearAboutUs") {
        setValidationErrors((prev: any) => ({
          ...prev,
          howDidYouHearAboutUsError: null,
        }));
      }
      setFormInputs((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const renderErrorMessage = (errorName: string) => {
    const error = validationErrors[errorName + "Error"];
    if (error)
      return <div className="mt-1 pl-2 text-red text-sm">{error}</div>;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    // validate input
    const errors = validate(formInputs);
    setValidationErrors(errors);
    // Check if there are any errors
    if (Object.values(errors).some((error) => error !== null)) {
      setIsSubmitting(false);
      setFormSubmitError(true);
      setTimeout(() => {
        setFormSubmitError(false);
      }, 1500);
      return;
    }

    const recaptchaSuccess = await googleRecaptcha(executeRecaptcha);
    // if recaptcha success, submit form to backend
    if (recaptchaSuccess) {
      const leadTracking = getLeadTrackingData();
      // upload resume to strapi media
      if (resumeFile) {
        const data = new FormData();
        data.append("files", resumeFile);
        const response = await fetch(`${getStrapiURL(`/api/upload`)}`, {
          method: "post",
          body: data,
        });

        // manually override formInputs resume to be the uploaded file url
        const uploadData = await response.json();
        formInputs.resume = uploadData[0].url;
      }

      let url = `${getStrapiURL(`/api/career-form-submission-datas`)}`; // api url based on selected subject
      let res = await postRequest(url, {
        formInputs,
        leadTracking,
        token: recaptchaSuccess, // add newToken into form POST request to do backend recaptcha verification
      });
      // console.log("status : ", res.status);
      if (res.status === 400) {
        setFormSubmitError(true);
        setTimeout(() => {
          setFormSubmitError(false);
        }, 1500);
        setIsSubmitting(false);
      } else {
        // console.log("submit success");
        const source = window.location.pathname.slice(1);

        const conversionMeta = {
          formType: "career_form",
          leadType: "job_application" as const,
          source,
          trafficSource: leadTracking.trafficSource,
        };

        // Push enhanced conversions data to GTM
        pushEnhancedConversionsData(formInputs, conversionMeta);

        // setFormSubmitted(true);
        setFormInputs({
          name: "",
          mobileNumber: "",
          email: "",
          expectedSalary: "",
          description: "",
          howDidYouHearAboutUs: "",
          resume: null,
        });
        setTimeout(() => {
          setIsSubmitting(false);
          setFormSubmitted(true);
          router.push(getThankYouUrl(conversionMeta));
        }, 2000);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    let allNotEmpty = true;

    Object.values(formInputs).forEach((value) => {
      if (value === "" || value === null) {
        allNotEmpty = false;
        return;
      }
    });
    setAllInputsNotEmpty(allNotEmpty);

  }, [formInputs]);

  return (
    <div
      className={`${formData
        ? "bg-darkPurple overflow-x-clip pt-16 z-10 relative"
        : ""
        }`}
    >
      {formData && <h2 className=" text-center">{formData.heading}</h2>}

      <div
        id="kd-form"
        className="kd-form relative px-8 pb-24 sm:pb-[7vh] lg:px-[17.188vw]"
      >
        <form className="relative grid grid-cols-1 md:grid-cols-2 gap-y-6">
          {formData &&
            formData.form.input.map(
              (inputs: any, index: number) => {
                if (inputs.inputType === "text") {
                  return (
                    <div
                      className="relative w-full"
                      key={index}
                    >
                      <InputHolder
                        input={inputs}
                        onUpdate={handleInputChange}
                      />
                      <div className="text-red font-bold absolute right-4">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (inputs.inputType === "multiselect") {
                  return (
                    <div className="relative" key={index}>
                      <MultiselectInputHolder
                        input={inputs}
                        onUpdateService={
                          handleInputChange
                        }
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (inputs.inputType === "description") {
                  return (
                    <div
                      className="relative w-full row-span-2"
                      key={index}
                    >
                      <Textarea
                        input={inputs}
                        onUpdate={handleInputChange}
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (
                  inputs.inputType === "select" &&
                  inputs.name == "howDidYouHearAboutUs"
                ) {
                  return (
                    <React.Fragment key={index}>
                      <div className="relative w-full row-span-2">
                        <SelectInputHolder
                          input={inputs}
                          onUpdate={handleInputChange}
                        />
                        <div className="text-red font-bold absolute right-4 z-0">
                          {!formInputs.howDidYouHearAboutUs?.startsWith(
                            "Others"
                          ) &&
                            !formInputs.howDidYouHearAboutUs?.startsWith(
                              "Others - "
                            ) &&
                            renderErrorMessage(
                              inputs.name
                            )}
                        </div>
                        {(formInputs.howDidYouHearAboutUs ===
                          "Others" ||
                          formInputs.howDidYouHearAboutUs?.startsWith(
                            "Others - "
                          )) && (
                            <div className="relative w-full row-span-2 pt-6">
                              <InputHolder
                                input={{
                                  name: "howDidYouHearAboutUsOther",
                                  placeholder:
                                    "Please specify...",
                                  label: "Please specify",
                                }}
                                onUpdate={
                                  handleInputChange
                                }
                              />
                              <div className="text-red font-bold absolute right-4">
                                {renderErrorMessage(
                                  inputs.name
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </React.Fragment>
                  );
                } else if (inputs.inputType === "select") {
                  return (
                    <div className="relative" key={index}>
                      <SelectInputHolder
                        input={inputs}
                        onUpdate={handleInputChange}
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                } else if (inputs.inputType === "file") {
                  return (
                    <div className="relative" key={index}>
                      <FileInputHolder
                        input={inputs}
                        onUpdate={handleInputChange}
                        setResumeFile={setResumeFile}
                      />
                      <div className="text-red font-bold absolute right-4 z-0">
                        {renderErrorMessage(
                          inputs.name
                        )}
                      </div>
                    </div>
                  );
                }
              }
            )}
        </form>

        <div className="flex flex-col items-center w-full mt-5">
          <span className="text-white text-sm md:text-base">
            By submitting, you agree to our{" "}
            <a
              className="text-cyan underline"
              href="/privacy"
              target="_blank"
            >
              Privacy Policy
            </a>
          </span>
        </div>

        {formData && (
          <div className="submit-holder flex md:items-end md:justify-end w-full mt-12 md:mt-20 md:px-4">
            <div
              className={`submit-button md:absolute ${isSubmitting
                ? "cursor-not-allowed pointer-events-none"
                : "cursor-pointer"
                } `}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <span
                className={`text-font-24 font-semibold bg-green px-9 py-4 rounded-full text-darkGrey leading-[1] flex`}
              >
                Submit
              </span>
            </div>
          </div>
        )}

        <div
          className={`absolute my-4 mx-auto bottom-0 left-0 right-0 z-10 flex w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-4 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] mt-6 pointer-events-none ${formSubmitted
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-5"
            } duration-300`}
        >
          <Image
            src={NotifiactionIcon}
            alt={""}
            width={26}
            height={26}
          />
          <span className="text-white text-font-18">
            Thank you for your enquiry! We&apos;ll respond shortly.
          </span>
        </div>

        <div
          className={`absolute my-4 mx-auto left-0 bottom-0 right-0 flex w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] ${formSubmitError
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-7"
            } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
        >
          <span className="text-sm text-white tracking-wider">
            Please fill in all the details
          </span>
        </div>

        <div
          className={`absolute my-4 mx-auto left-0 bottom-0 right-0 flex w-fit h-fit items-center gap-3 backdrop-blur-xl px-6 py-3 rounded-xl bg-gradient-to-b from-[#8067b4] to-[#512e99] ${isSubmitting
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-7"
            } duration-300 z-20 pointer-events-none shadow-red-500 drop-shadow-[0_0_10px_rgba(220,38,38,0.5)]`}
        >
          <span className="text-sm text-white tracking-wider">
            Sending your details now. Thanks for waiting!
          </span>
        </div>
      </div>
    </div>
  );
}
